import React, { memo, useState, useCallback, CSSProperties, useEffect } from 'react';
import { exceptThisSymbols } from '../../../constants/constants';
import SingleOtpInput from './SingleInput';
import Styles from './OtpInput.module.css';
export interface OTPInputProps {
    length: number;
    onChangeOTP: (otp: string) => void;
    value: string;
    autoFocus?: boolean;
    istouched?: boolean;
    isNumberInput?: boolean;
    disabled?: boolean;
    onPaste?: (e: string) => void;
    style?: CSSProperties;
    className?: string;
    hasError?: boolean;
    inputStyle?: CSSProperties;
    inputClassName?: string;
    onChange?: (val: string) => void;
    errorMessage?: string;
}

const OtpInput = (props: OTPInputProps) => {
    const { value, length, isNumberInput, istouched, autoFocus, disabled, onChangeOTP, onChange, onPaste, inputClassName, inputStyle, hasError, errorMessage, ...rest } = props;
    const [activeInput, setActiveInput] = useState(0);
    const [otpValues, setOTPValues] = useState((value && value.split('')) || Array<string>(length).fill(''));

    useEffect(() => {
        if (value) {
            setOTPValues(value.split(''))
        }
    }, [value])


    // Helper to return OTP from inputs
    const handleOtpChange = useCallback(
        (otp: string[]) => {
            const otpValue = otp.join('');
            onChangeOTP(otpValue);
            setOTPValues(otpValue.replace(/\s/g, '').split(''));
        },
        [onChangeOTP],
    );

    // Helper to return value with the right type: 'text' or 'number'
    const getRightValue = useCallback(
        (str: string) => {
            const changedValue = str;

            if (!isNumberInput || !changedValue) {
                return changedValue;
            }

            return Number(changedValue) >= 0 ? changedValue : '';
        },
        [isNumberInput],
    );

    // Change OTP value at focussing input
    const changeCodeAtFocus = useCallback(
        (str: string) => {
            const updatedOTPValues = [...otpValues];
            updatedOTPValues[activeInput] = str[0] || '';
            setOTPValues(updatedOTPValues);
            handleOtpChange(updatedOTPValues);
        },
        [activeInput, handleOtpChange, otpValues],
    );

    // Focus `inputIndex` input
    const focusInput = useCallback(
        (inputIndex: number) => {
            const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
            setActiveInput(selectedIndex);
        },
        [length],
    );

    const focusPrevInput = useCallback(() => {
        focusInput(activeInput - 1);
    }, [activeInput, focusInput]);

    const focusNextInput = useCallback(() => {
        focusInput(activeInput + 1);
    }, [activeInput, focusInput]);

    // Handle onFocus input
    const handleOnFocus = useCallback(
        (index: number) => () => {
            focusInput(index);
        },
        [focusInput],
    );

    // Handle onChange value for each input
    const handleOnChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = getRightValue(e.currentTarget.value);
            if (!val) {
                e.preventDefault();
                return;
            }
            changeCodeAtFocus(val);
            focusNextInput();
        },
        [changeCodeAtFocus, focusNextInput, getRightValue],
    );

    // Handle onBlur input
    const onBlur = useCallback(() => {
        setActiveInput(-1);
    }, []);

    // Handle onKeyDown input
    const handleOnKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const pressedKey = e.key;

            if (exceptThisSymbols.includes(e.key)) {
                e.preventDefault();
            }

            switch (pressedKey) {
                case 'Backspace':
                case 'Delete': {
                    e.preventDefault();
                    if (otpValues[activeInput]) {
                        changeCodeAtFocus('');
                    } else {
                        focusPrevInput();
                    }
                    break;
                }
                case 'ArrowLeft': {
                    e.preventDefault();
                    focusPrevInput();
                    break;
                }
                case 'ArrowRight': {
                    e.preventDefault();
                    focusNextInput();
                    break;
                }
                default: {
                    if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
                        e.preventDefault();
                    }

                    break;
                }
            }
        },
        [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues],
    );

    const handleOnPaste = useCallback(
        (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            const pastedData = e.clipboardData
                .getData('text/plain')
                .trim()
                .replace(/\s/g, '')
                .slice(0, length - activeInput)
                .split('');

            if (pastedData.length > 0) {
                const updatedOTPValues = [...otpValues];

                let nextFocusIndex = activeInput;

                for (let i = activeInput; i < length && pastedData.length > 0; i++) {
                    const char = getRightValue(pastedData.shift() || '');
                    if (char) {
                        updatedOTPValues[i] = char;
                        nextFocusIndex = i;
                    }
                }

                setOTPValues(updatedOTPValues);
                setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
                handleOtpChange(updatedOTPValues);
            }
        },
        [activeInput, getRightValue, handleOtpChange, length, otpValues]
    );


    return (
        <div {...rest}>
            {Array(length)
                .fill('')
                .map((_, index) => (
                    <SingleOtpInput
                        key={`SingleInput-${index}`}
                        type={isNumberInput ? 'number' : 'text'}
                        focus={activeInput === index}
                        value={(otpValues && otpValues[index]) || ''}
                        autoFocus={autoFocus}
                        istouched={istouched}
                        onFocus={handleOnFocus(index)}
                        onChange={handleOnChange}
                        onKeyDown={handleOnKeyDown}
                        onBlur={onBlur}
                        onPaste={handleOnPaste}
                        style={inputStyle}
                        className={`${inputClassName}`}
                        // className={`${inputClassName} ${hasError ? `${Styles.error}` : ''}`}
                        disabled={disabled}
                        name="masterCode"
                    />
                ))}
        </div>
    );
};

const OTPInput = memo(OtpInput);
export default OTPInput;
